import { Transform } from 'class-transformer';
import {
  IsBoolean,
  IsIn,
  IsNotEmpty,
  IsOptional,
  IsString,
  Matches,
  MaxLength,
} from 'class-validator';
import { PREPACKAGED_MARKET_COUNTRY_CODES } from '../../label/prepackaged-country-names';
import { IsCap } from '../../validation/is-cap.decorator';
import { IsPartitaIvaFormat } from '../../validation/is-partita-iva.decorator';
import { IsPec, IsSdiCode } from '../../validation/is-pec-sdi.decorator';
import { IsPhone } from '../../validation/is-phone';
import { OptionalStringTransform } from '../../validation/optional-string.transformer';
import { OptionalTrimmedStringTransform } from '../../validation/optional-trimmed-string.transformer';
import { PROVINCIA_REGEX } from '../../validation/provincia';

export const MANAGED_CLIENTS_CLAUSE_VERSION = '2026-10-v1';
export const MAX_MANAGED_CLIENTS_PER_CONSULTANT = 3000;

function uppercase({ value }: { value: unknown }): unknown {
  return typeof value === 'string' ? value.toUpperCase() : value;
}

export class CreateManagedClientDto {
  @IsString()
  @Transform(({ value }) => (typeof value === 'string' ? value.trim() : value))
  @IsNotEmpty()
  @MaxLength(200)
  companyName!: string;

  @IsString()
  @IsOptional()
  @OptionalStringTransform()
  @IsPartitaIvaFormat()
  vatNumber?: string;

  @IsBoolean()
  @IsOptional()
  clauseAccepted?: boolean;

  /** Distinct legal name; when absent the company name is used. */
  @IsString()
  @IsOptional()
  @OptionalTrimmedStringTransform()
  @MaxLength(200)
  ragioneSociale?: string;

  @IsString()
  @IsOptional()
  @OptionalTrimmedStringTransform()
  @MaxLength(50)
  codiceFiscale?: string;

  @IsString()
  @IsOptional()
  @OptionalTrimmedStringTransform()
  @MaxLength(200)
  indirizzoVia?: string;

  @IsString()
  @IsOptional()
  @OptionalTrimmedStringTransform()
  @MaxLength(100)
  indirizzoCitta?: string;

  @IsString()
  @IsOptional()
  @OptionalTrimmedStringTransform()
  @IsCap()
  indirizzoCap?: string;

  @IsString()
  @IsOptional()
  @OptionalTrimmedStringTransform()
  @Transform(uppercase)
  @Matches(PROVINCIA_REGEX, { message: 'Provincia non valida' })
  indirizzoProvincia?: string;

  @IsString()
  @IsOptional()
  @OptionalTrimmedStringTransform()
  @MaxLength(254)
  @IsPec()
  pec?: string;

  @IsString()
  @IsOptional()
  @OptionalTrimmedStringTransform()
  @IsSdiCode()
  codiceSdi?: string;

  @IsOptional()
  @OptionalTrimmedStringTransform()
  @IsPhone()
  phone?: string;

  /** ISO 3166-1 alpha-2 della sede legale. */
  @IsString()
  @IsOptional()
  @OptionalTrimmedStringTransform()
  @Transform(uppercase)
  @IsIn(PREPACKAGED_MARKET_COUNTRY_CODES)
  countryCode?: string;
}

export interface CreateManagedClientResponse {
  tenantId: string;
  tenantName: string;
}
